import { useEffect, useRef } from 'react'
import { jwtDecode } from 'jwt-decode'
import * as authService from 'services/auth'
import { AuthResponse } from 'models/response'
import { NotificationItem } from 'utils/notification-helpers'
import { EventAttendeeState } from 'graphql/generated/scheme'
import {
	publishTicketCheckin,
	publishAttendeeState,
	publishTicketUpdated,
	publishScanAdded,
} from 'pubsub/pubsub-events'

const BACKOFF_INITIAL = 1_000
const BACKOFF_MAX = 30_000
const NOTIFICATION_FETCH_DELAY = 5_000

interface WsMessage {
	type: string
	data?: unknown
}

// Runtime type guard: narrows `unknown` to a plain object so handlers can read
// fields with `typeof` checks instead of `as` casts.
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value != null

// Dispatch by eventType — add a key to extend, no branch changes. Each handler
// receives the raw payload as `unknown` and narrows it with `typeof` before
// publishing.
const EVENT_HANDLERS: Record<string, (data: unknown) => void> = {
	CHECKIN_SUCCESS: data => {
		if (!isRecord(data) || typeof data.eventId !== 'string') return
		publishTicketCheckin({ eventId: data.eventId })
	},
	ATTENDEE_STATE_UPDATED: data => {
		if (
			!isRecord(data) ||
			typeof data.eventId !== 'string' ||
			typeof data.userId !== 'string' ||
			typeof data.attendeeState !== 'string'
		)
			return
		publishAttendeeState({
			eventId: data.eventId,
			userId: data.userId,
			attendeeState: data.attendeeState as EventAttendeeState,
		})
	},
	TICKET_UPDATED: data => {
		if (!isRecord(data) || typeof data.eventId !== 'string') return
		publishTicketUpdated({
			eventId: data.eventId,
			eventTicketId: typeof data.eventTicketId === 'string' ? data.eventTicketId : undefined,
		})
	},
	// One row was added to eventScanRecords (backend `scanRecordAdded`). Best-effort,
	// per-event; treated as a "refresh the scan table" signal, not a guaranteed ledger.
	SCAN_ADDED: data => {
		if (!isRecord(data) || typeof data.eventId !== 'string' || typeof data.ticketId !== 'string') return
		publishScanAdded({
			eventId: data.eventId,
			ticketId: data.ticketId,
			scanningZoneId: typeof data.scanningZoneId === 'string' ? data.scanningZoneId : undefined,
			scannedAt: typeof data.scannedAt === 'string' ? data.scannedAt : undefined,
			scannedByUserId: typeof data.scannedByUserId === 'string' ? data.scannedByUserId : undefined,
		})
	},
}

export interface UseNotificationWebSocketOptions {
	skip: boolean
	onUpdate: (notification?: NotificationItem) => void
}

export function useNotificationWebSocket({ skip, onUpdate }: UseNotificationWebSocketOptions) {
	const wsRef = useRef<WebSocket | null>(null)
	const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const fetchDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const backoffRef = useRef<number>(BACKOFF_INITIAL)
	const unmountedRef = useRef<boolean>(false)
	// Stable ref so the connect closure never captures a stale onUpdate.
	// onUpdate is intentionally excluded from the [skip] effect dep array below.
	const onUpdateRef = useRef<(notification?: NotificationItem) => void>(onUpdate)
	useEffect(() => {
		onUpdateRef.current = onUpdate
	})

	useEffect(() => {
		if (skip) return

		unmountedRef.current = false

		// Dispatch by frame type — add a key to extend, no branch changes.
		const MESSAGE_HANDLERS: Record<string, (msg: WsMessage) => void> = {
			NOTIFICATION_UPDATE: msg => {
				// Delay fetch to allow the backend write to propagate before reading.
				// Debounced: rapid bursts only trigger one refetch.
				if (fetchDelayTimerRef.current) clearTimeout(fetchDelayTimerRef.current)
				fetchDelayTimerRef.current = setTimeout(() => {
					onUpdateRef.current(msg.data as NotificationItem | undefined)
				}, NOTIFICATION_FETCH_DELAY)
			},
			EVENT_UPDATE: msg => {
				if (!isRecord(msg.data) || typeof msg.data.eventType !== 'string') return
				EVENT_HANDLERS[msg.data.eventType]?.(msg.data)
			},
		}

		const handleMessage = (msg: WsMessage) => {
			MESSAGE_HANDLERS[msg.type]?.(msg)
		}

		async function connect() {
			if (unmountedRef.current) return

			let token = authService.getToken()
			if (!token) return

			// Refresh token if it expires within 60 seconds
			try {
				const { exp } = jwtDecode<{ exp: number }>(token)
				if (exp - Date.now() / 1000 < 60) {
					const refreshToken = authService.getRefreshToken()
					if (refreshToken) {
						try {
							// The Axios interceptor returns response.data directly,
							// so the resolved value is AuthResponse (not AxiosResponse<AuthResponse>).
							// The Axios interceptor unwraps response.data at runtime, but TypeScript
							// cannot model interceptor transforms — same cast used in api/index.ts:36.
							const response = (await authService.refresh(refreshToken)) as any as AuthResponse
							token = response.token
							authService.setToken(token)
							authService.setRefreshToken(response.refreshToken)
						} catch {
							// Refresh failed — abort this connect attempt to prevent an auth loop
							return
						}
					}
				}
			} catch {
				// jwtDecode failed — proceed with existing token
			}

			const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
			const url = `${protocol}//${window.location.host}/ws?token=${encodeURIComponent(token)}`

			const ws = new WebSocket(url)
			wsRef.current = ws

			// Track whether onopen fired for this specific connection attempt.
			// If it never fires, the server rejected the upgrade (likely auth error).
			let didOpen = false

			ws.onopen = () => {
				didOpen = true
				backoffRef.current = BACKOFF_INITIAL
			}

			ws.onmessage = event => {
				try {
					handleMessage(JSON.parse(event.data as string))
				} catch {
					// ignore malformed frames
				}
			}

			ws.onerror = () => {
				ws.close()
			}

			ws.onclose = event => {
				if (unmountedRef.current) return
				// 1000 = normal close, 1001 = going away (tab close) — do not reconnect
				if (event.code === 1000 || event.code === 1001) return
				// 4xxx = application-level rejection (auth error, policy violation, etc.) OR
				// the upgrade was refused before onopen fired — stop reconnecting in both cases
				// to avoid hammering the server with bad credentials.
				if (!didOpen || event.code >= 4000) return

				const delay = backoffRef.current
				backoffRef.current = Math.min(delay * 2, BACKOFF_MAX)

				reconnectTimerRef.current = setTimeout(async () => {
					await connect()
					onUpdateRef.current() // catch up on any notifications missed while disconnected
				}, delay)
			}
		}

		connect()

		const handleBeforeUnload = () => wsRef.current?.close(1001, 'tab closing')
		window.addEventListener('beforeunload', handleBeforeUnload)

		return () => {
			unmountedRef.current = true
			window.removeEventListener('beforeunload', handleBeforeUnload)
			if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
			if (fetchDelayTimerRef.current) clearTimeout(fetchDelayTimerRef.current)
			wsRef.current?.close(1000, 'unmount')
			wsRef.current = null
		}
		// onUpdate is intentionally excluded — accessed via stable onUpdateRef above
	}, [skip])
}
